<template>
  <div
    class="HotelSectionsNavigation Layout"
    :class="{ 'HotelSectionsNavigation--inverse': inverse }">
    <ai-separator margin="none" :color="separatorColor" />
    <div ref="container" class="HotelSectionsNavigation-items">
      <div
        v-if="leftArrowVisible"
        ref="leftArrow"
        class="HotelSectionsNavigation-arrowContainer HotelSectionsNavigation-arrowContainer--Left"
        @click="onLeftArrowClick">
        <button
          class="HotelSectionsNavigation-arrowButton HotelSectionsNavigation-arrowButton--Left">
          <ai-icon
            name="chevron-left"
            class="HotelSectionsNavigation-arrowButtonIcon"
            :size="16"
            :description="$t('accessibility.description.leftButton')" />
        </button>
      </div>

      <div
        ref="slider"
        role="list"
        class="HotelSectionsNavigation-itemsContainer"
        :style="{ transform: `translateX(${sliderTransform}px)` }">
        <hotel-sections-navigation-item
          v-for="item in visibleSections"
          ref="sliderItems"
          :key="item.id ?? ''"
          :href="'#' + item.id"
          :active="item.id === activeSection"
          :label="item.label"
          :inverse="inverse" />
      </div>

      <div
        v-if="rightArrowVisible"
        ref="rightArrow"
        class="HotelSectionsNavigation-arrowContainer HotelSectionsNavigation-arrowContainer--Right"
        @click="onRightArrowClick">
        <button class="HotelSectionsNavigation-arrowButton">
          <ai-icon
            class="HotelSectionsNavigation-arrowButtonIcon"
            name="chevron-right"
            :size="16"
            :description="$t('accessibility.description.rightButton')" />
        </button>
      </div>
    </div>

    <ai-separator margin="none" :color="separatorColor" />
  </div>
</template>

<script setup lang="ts">
type HotelSection = {
  id: string;
  label: string;
  visible: boolean;
};

type HotelSectionsNavigationProps = {
  inverse?: boolean;
  sections: HotelSection[];
};

const props = withDefaults(defineProps<HotelSectionsNavigationProps>(), {
  inverse: false,
});

const container = ref<HTMLDivElement | undefined>();
const slider = ref<HTMLDivElement | undefined>();
const sliderItems = ref<ComponentPublicInstance[] | undefined>();
const leftArrow = ref<HTMLDivElement | undefined>();
const rightArrow = ref<HTMLDivElement | undefined>();

const leftArrowVisible = ref<boolean>(false);
const rightArrowVisible = ref<boolean>(false);
const sliderTransform = ref<number>(0);

const onLeftArrowClick = () => {
  const leftArrowBox = leftArrow.value?.getBoundingClientRect();
  if (!leftArrowBox) {
    return;
  }

  const reversedSliderItems = [...(sliderItems.value ?? [])].reverse();
  let transformValue = 0;
  let index = 0;
  const lastItemIndex = reversedSliderItems.length - 1;

  for (const sliderItem of reversedSliderItems) {
    const sliderItemElement: HTMLElement = sliderItem.$el;
    const sliderItemBox = sliderItemElement.getBoundingClientRect();
    if (index === lastItemIndex) {
      transformValue = -sliderTransform.value;
      sliderTransform.value = 0;
      break;
    }

    index++;
    if (sliderItemBox.left + sliderItemBox.width / 2 < leftArrowBox.right) {
      transformValue = leftArrowBox.right - sliderItemBox.left;
      sliderTransform.value += transformValue;
      break;
    }
  }

  calculateArrowsVisibility(transformValue);
};

const onRightArrowClick = () => {
  const rightArrowBox = rightArrow.value?.getBoundingClientRect();
  if (!rightArrowBox) {
    return;
  }

  let transformValue = 0;
  for (const sliderItem of sliderItems.value ?? []) {
    const sliderItemElement: HTMLElement = sliderItem.$el;
    const sliderItemBox = sliderItemElement.getBoundingClientRect();

    if (sliderItemBox.right - sliderItemBox.width / 2 > rightArrowBox.left) {
      transformValue = rightArrowBox.left - sliderItemBox.right;
      sliderTransform.value += transformValue;
      break;
    }
  }

  calculateArrowsVisibility(transformValue);
};

const calculateArrowsVisibility = (transformValue?: number) => {
  const navigationBox = slider.value?.getBoundingClientRect();
  if (!navigationBox || !container.value || !sliderItems.value) {
    return false;
  }

  const lastItem = sliderItems.value[sliderItems.value.length - 1];

  const right =
    lastItem.$el.getBoundingClientRect().right + (transformValue || 0);

  rightArrowVisible.value =
    right > container.value.getBoundingClientRect().right;
  leftArrowVisible.value = sliderTransform.value < 0;
};

const visibleSections = computed(() => {
  return props.sections.filter((section: HotelSection) => section.visible);
});

const activeSection = ref<string>(visibleSections.value[0].id);

const separatorColor = computed(() => {
  return props.inverse ? 'neutral' : 'stratos-light';
});

onMounted(() => {
  calculateArrowsVisibility();

  if (process.client) {
    window.addEventListener('scroll', onScroll);
    window.addEventListener('resize', onResize);
  }
});

onBeforeUnmount(() => {
  if (process.client) {
    window.removeEventListener('scroll', onScroll);
    window.removeEventListener('resize', onResize);
  }
});

const onResize = () => {
  sliderTransform.value = 0;
  calculateArrowsVisibility();
};

const onScroll = () => {
  if (!process.client) {
    return;
  }

  const scrollPos = window.scrollY || document.documentElement.scrollTop;

  let headerHeight = 0;
  const headerBoundingRect = document
    .getElementsByClassName('DefaultLayout-header')
    ?.item(0)
    ?.getBoundingClientRect();
  headerHeight = headerBoundingRect?.height ?? headerHeight;

  let currentSection: string = visibleSections.value[0].id;
  for (const section of visibleSections.value) {
    const sectionElement = document.getElementById(section.id);
    if (!sectionElement) {
      continue;
    }

    if (sectionElement.offsetTop - headerHeight <= scrollPos) {
      currentSection = section.id;
    }
  }

  activeSection.value = currentSection;
};
</script>

<style scoped lang="scss">
@use '@/assets/styles/utilities/colors';
@use '@/assets/styles/utilities/constants';
@use '@/assets/styles/utilities/mq';
@use '@/assets/styles/utilities/functions' as func;

.HotelSectionsNavigation {
  width: 100%;
  height: func.calcRem(54);
  display: none;
  margin-top: func.calcRem(16);

  @media (mq.$from-medium) {
    display: block;
  }
}

.HotelSectionsNavigation-items {
  position: relative;
  display: flex;
  align-items: center;
  flex-direction: column;
  overflow: hidden;
  width: 100%;
  height: 100%;
}

.HotelSectionsNavigation-margin {
  display: none;

  @media (mq.$from-medium) {
    display: block;
  }
}

.HotelSectionsNavigation-itemsContainer {
  display: flex;
  flex-direction: row;
  align-items: center;
  vertical-align: baseline;
  margin: auto;
  transition: transform 500ms;
  gap: constants.$margin-01;

  @media (mq.$from-large) {
    gap: constants.$margin-02;
    padding: constants.$padding-00;
  }
}

.HotelSectionsNavigation-arrowContainer {
  display: flex;
  position: absolute;
  top: 0;
  width: func.calcRem(calc(32 + 16)); // 32 base button + margins
  justify-content: center;
  align-items: center;
  z-index: 1;
  pointer-events: none;
  height: 100%;
  backdrop-filter: blur(100px);

  &--Left {
    left: 0;
  }

  &--Right {
    right: 0;
  }
}

.HotelSectionsNavigation-arrowButton {
  width: func.calcRem(32);
  height: func.calcRem(32);
  border-radius: 100%;
  background-color: colors.$gold-900;
  display: flex;
  justify-content: center;
  align-items: center;
  pointer-events: all;
}

.HotelSectionsNavigation-arrowButtonIcon {
  color: white;
}
</style>
